import { useEffect, useRef, type ReactNode } from 'react';

/**
 * A small, accessible modal dialog. Focus traps to the dialog while open,
 * Escape closes it, and the surrounding page is aria-hidden so screen
 * readers don't read through to the matches list behind it. The visual
 * idiom is the warm-editorial design — a paper card on a quiet wash, with
 * Fraunces for the title.
 */
export function Modal({
  title,
  onClose,
  children,
  labelledById,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  labelledById?: string;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const lastFocused = useRef<HTMLElement | null>(null);
  const id = labelledById ?? 'modal-title';

  useEffect(() => {
    lastFocused.current = document.activeElement as HTMLElement | null;
    // Move focus into the dialog so keyboard / screen-reader users land here.
    const node = dialogRef.current;
    const firstFocusable = node?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    firstFocusable?.focus();
    return () => lastFocused.current?.focus();
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    document.addEventListener('keydown', onKey);
    // Prevent the page behind from scrolling on mobile when the modal is up.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={id} className="modal-title">
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}
